// What needs action on the Ops Dashboard — BL-38's strip and BL-20's column.
//
// ⚠ ONE ENGINE FOR THE DASHBOARD AND THE MAP. Both call `buildLiveAttention`
// with the same reads, so "needs action" cannot mean one thing on the first
// screen Bam opens and another on the map he lives in. Leon's call (admin
// overhaul intake, W5): "strip on the dashboard, full attention on the map".
//
// Cross-hub roles only: the bookings come from the command feed, which joins
// client names a hub manager structurally cannot read (data-wall.md).

import { useMemo } from "react";
import {
  attentionByBookingId,
  buildLiveAttention,
  toNoShowAttentionInputs,
  type LiveAttentionItem,
} from "@/features/live-map/live-map.attention";
import {
  useLiveMapContext,
  useLiveMapNoShows,
  useLiveMapPendingCash,
  useTherapistReturnWaits,
} from "@/features/live-map/live-map.hooks";
import { staffStatusView } from "@/features/live-map/live-map.status";
import { useKycAwaiting } from "@/features/kyc/kyc.hooks";
import { useOpenSosAlerts } from "@/features/sos/sos.hooks";
import type { CommandBooking } from "./command-center.hooks";
import { useHubsList } from "./ops-dashboard.hooks";
import { toPaymentAttentionInputs, usePaymentExceptions } from "./payment-exceptions.hooks";

export interface OpsAttention {
  items: LiveAttentionItem[];
  byBookingId: Map<string, LiveAttentionItem[]>;
  /** Refs named by an SOS not yet resolved — shown in the table, not the strip
   * (the SOS cards already sit at the top of every page). */
  sosRefs: Set<string>;
  loading: boolean;
  /** A failed read is not a clear board — nothing has been ruled out. */
  failed: boolean;
}

export function useOpsAttention(
  bookings: readonly CommandBooking[] | undefined,
  bookingsFailed: boolean,
  /** Only superadmin holds a grant on the KYC tables (0070). */
  withKyc: boolean,
): OpsAttention {
  const kyc = useKycAwaiting(withKyc);
  const contextQuery = useLiveMapContext();
  const noShowsQuery = useLiveMapNoShows();
  const pendingCashQuery = useLiveMapPendingCash();
  const paymentQuery = usePaymentExceptions();
  const returnWaitsQuery = useTherapistReturnWaits();
  const sosQuery = useOpenSosAlerts();
  const { data: hubs } = useHubsList();
  const nowBucket = Math.floor(Date.now() / 60_000);

  const context = contextQuery.data;
  const noShows = noShowsQuery.data;
  const pendingCash = pendingCashQuery.data;
  const payments = paymentQuery.data;
  const returnWaits = returnWaitsQuery.data;
  const sos = sosQuery.data;

  return useMemo(() => {
    const ready =
      bookings &&
      context &&
      noShows &&
      pendingCash &&
      payments &&
      (!withKyc || kyc.ready || kyc.failed);
    const items = ready
      ? buildLiveAttention({
          nowMs: nowBucket * 60_000,
          hubNames: new Map((hubs ?? []).map((hub) => [hub.id, hub.name])),
          bookings,
          // Only engine statuses can raise "lost connection", and those never
          // read the trips fallback — so `false` here changes nothing.
          staff: context.clockedInStaff.map((member) => ({
            id: member.id,
            name: member.name,
            hubId: member.hubId,
            status: staffStatusView(member, false),
          })),
          noShows: toNoShowAttentionInputs(noShows),
          paymentExceptions: toPaymentAttentionInputs(payments),
          pendingCash,
          returnWaits,
          // A failed KYC read is reported through `failed`; it does not hold
          // back every other item.
          kycAwaiting: withKyc && kyc.ready ? { count: kyc.count, oldestAt: kyc.oldestAt } : null,
        })
      : [];
    return {
      items,
      byBookingId: attentionByBookingId(items),
      sosRefs: new Set((sos ?? []).flatMap((alert) => alert.bookingRefs)),
      loading: !ready,
      failed:
        bookingsFailed ||
        contextQuery.isError ||
        // The staff feed answering with an error is how a lost connection
        // would go unreported, so it counts as a failed read too.
        Boolean(context?.staffLocationError) ||
        noShowsQuery.isError ||
        pendingCashQuery.isError ||
        paymentQuery.isError ||
        (withKyc && kyc.failed),
    };
  }, [
    bookings,
    bookingsFailed,
    context,
    contextQuery.isError,
    hubs,
    kyc.count,
    kyc.failed,
    kyc.oldestAt,
    kyc.ready,
    noShows,
    noShowsQuery.isError,
    nowBucket,
    payments,
    paymentQuery.isError,
    pendingCash,
    pendingCashQuery.isError,
    sos,
    withKyc,
  ]);
}
